import { useEffect, useMemo, useState } from "react";
import { catColors } from "@/domain/categories";
import type { Category, CategoryGroup, Layers, RapidFireMarks, RenderKeycap } from "@/domain/types";
import { ComboKbds } from "@/render/components/ComboKbds";
import { RapidFireGlyph } from "@/render/components/RapidFireGlyph";
import { buildListGroups, type ListRow, rowKey } from "./groups";

interface BindingsListProps {
  layers: Layers;
  keycaps: RenderKeycap[];
  categories: Record<string, Category>;
  groups: CategoryGroup[];
  /** The isolated group id; its Categories stay lit on the board, the rest dim. */
  dimId: string | null;
  /** The row currently hovered (mirrors the board highlight). */
  hoverKey: ListRow | null;
  /** Rapid-fire marks when the overlay is on, else null; flags chips. */
  marks?: RapidFireMarks | null;
  onHover: (row: ListRow) => void;
  onLeave: () => void;
  onSelect: (row: ListRow) => void;
  onIsolate: (id: string) => void;
  onCollapse: () => void;
  /** The codes matched by the active search, or null when not searching: the board flashlight. */
  onSearch?: (codes: string[] | null) => void;
}

/** The Bindings panel: searchable, Category-grouped rows that drive the board. */
export function BindingsList({
  layers,
  keycaps,
  categories,
  groups,
  dimId,
  hoverKey,
  marks = null,
  onHover,
  onLeave,
  onSelect,
  onIsolate,
  onCollapse,
  onSearch,
}: BindingsListProps) {
  const [query, setQuery] = useState("");
  // empty set = every group collapsed, the prototype's default
  const [expanded, setExpanded] = useState<ReadonlySet<string>>(() => new Set());

  const listGroups = useMemo(
    () => buildListGroups(layers, keycaps, categories, groups, query, marks),
    [layers, keycaps, categories, groups, query, marks],
  );
  const total = listGroups.reduce((sum, g) => sum + g.rows.length, 0);
  const searching = query.trim().length > 0;

  // the search flashlight: the codes of every matching row joined into a stable
  // key, or null when not searching OR nothing matches. A zero-match query must only
  // show "no matches" in the list, never dim the whole board (codes carry no spaces,
  // so a space-joined key round-trips). Keying on the string also keeps a modifier
  // press - which recomposes keycaps and rebuilds listGroups to the same codes - from
  // churning the parent on array identity alone.
  const matchedKey = useMemo(() => {
    if (!searching) return null;
    const codes = [
      ...new Set(listGroups.flatMap((g) => g.rows.flatMap((r) => r.keys.map((k) => k.code)))),
    ];
    return codes.length ? codes.join(" ") : null;
  }, [searching, listGroups]);
  useEffect(() => {
    onSearch?.(matchedKey ? matchedKey.split(" ") : null);
  }, [matchedKey, onSearch]);
  // clear the flashlight when the panel unmounts (collapsed), so the board un-dims
  useEffect(() => () => onSearch?.(null), [onSearch]);

  const toggle = (id: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (!next.delete(id)) next.add(id);
      return next;
    });

  return (
    <aside className="kl-list">
      <div className="kl-list-card">
        <div className="kl-list-hd">
          <div className="kl-list-title">
            <span>Bindings</span>
            <span className="kl-list-count">{total}</span>
            <button
              type="button"
              className="kl-list-collapse"
              title="Hide bindings"
              aria-label="Hide bindings"
              onClick={onCollapse}
            >
              <svg
                aria-hidden="true"
                width="15"
                height="15"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M13 6l6 6-6 6M6 6l6 6-6 6" />
              </svg>
            </button>
          </div>
          <div className="kl-search">
            <svg
              aria-hidden="true"
              width="13"
              height="13"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.2"
            >
              <circle cx="11" cy="11" r="7" />
              <path d="M21 21l-4.3-4.3" strokeLinecap="round" />
            </svg>
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search bindings…"
              aria-label="Search bindings"
            />
            {query ? (
              <button
                type="button"
                className="kl-search-x"
                onClick={() => setQuery("")}
                aria-label="Clear"
              >
                ✕
              </button>
            ) : null}
          </div>
        </div>
        <div className="kl-list-body">
          {listGroups.length === 0 ? (
            <div className="kl-list-empty">No bindings match “{query}”.</div>
          ) : (
            listGroups.map((g) => {
              const isolated = dimId === g.id;
              const dimmed = dimId !== null && !isolated;
              const open = searching || isolated || expanded.has(g.id);
              return (
                <section key={g.id} className={dimmed ? "kl-list-grp dimmed" : "kl-list-grp"}>
                  <div className={open ? "kl-list-grp-hd open" : "kl-list-grp-hd"}>
                    <button
                      type="button"
                      className="kl-grp-main"
                      onClick={() => toggle(g.id)}
                      aria-expanded={open}
                    >
                      <svg
                        aria-hidden="true"
                        className="kl-grp-chev"
                        width="11"
                        height="11"
                        viewBox="0 0 16 16"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2.2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M6 4l4 4-4 4" />
                      </svg>
                      <span className="kl-list-dot" style={{ background: g.accent }} />
                      <span className="kl-list-grp-l">{g.label}</span>
                      <span className="kl-list-grp-c">{g.rows.length}</span>
                    </button>
                    <button
                      type="button"
                      className={isolated ? "kl-grp-iso on" : "kl-grp-iso"}
                      title={isolated ? "Show all on keyboard" : "Isolate on keyboard"}
                      aria-pressed={isolated}
                      onClick={() => onIsolate(g.id)}
                    >
                      <svg
                        aria-hidden="true"
                        width="13"
                        height="13"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                      >
                        <circle cx="12" cy="12" r="3" />
                        <path d="M12 5V3M12 21v-2M5 12H3M21 12h-2" strokeLinecap="round" />
                      </svg>
                    </button>
                  </div>
                  {open ? (
                    <div className="kl-list-rows">
                      {g.rows.map((r) => {
                        const isHover = hoverKey !== null && rowKey(hoverKey) === rowKey(r);
                        return (
                          <button
                            type="button"
                            key={rowKey(r)}
                            className={`kl-list-row${isHover ? " hot" : ""}${r.shadowed ? " sh" : ""}`}
                            onMouseEnter={() => onHover(r)}
                            onMouseLeave={onLeave}
                            onClick={() => onSelect(r)}
                          >
                            <span className="kl-list-keys">
                              <ComboKbds combo={r.combo} />
                              {r.keys.map((k) => (
                                <kbd key={k.code} className={k.rapidFire ? "kl-rf" : undefined}>
                                  {k.legend}
                                  {k.rapidFire ? <RapidFireGlyph /> : null}
                                </kbd>
                              ))}
                            </span>
                            <span className="kl-list-name">
                              {r.name}
                              {r.shadowed ? (
                                <span
                                  className="kl-list-deferred"
                                  title="Deferred: this key shows another binding; this one shares it"
                                >
                                  deferred
                                </span>
                              ) : null}
                            </span>
                            <span
                              className="kl-list-cdot"
                              style={{ background: catColors(categories[r.category]).accent }}
                            />
                          </button>
                        );
                      })}
                    </div>
                  ) : null}
                </section>
              );
            })
          )}
        </div>
      </div>
    </aside>
  );
}
